import type { AgentChangeset, AgentFileChange } from '@/_contract/agentChat';

export const CHANGESET_LABELS = {
    session: 'Agent-recorded edits (session)',
    turn: 'Agent-recorded edits (latest turn)',
    uncommitted: 'Workspace changes (uncommitted)',
};

export function isRepositoryPath(value: unknown): value is string {
    return typeof value === 'string' && value.length > 0 && !/[:\\\u0000-\u001f]/.test(value)
        && value.split('/').every(part => !!part && part !== '.' && part !== '..');
}

function isFileChange(value: unknown): value is AgentFileChange {
    if (!value || typeof value !== 'object' || !('kind' in value)) return false;
    const before = 'beforePath' in value ? value.beforePath : undefined;
    const after = 'afterPath' in value ? value.afterPath : undefined;
    if ((before !== undefined && !isRepositoryPath(before)) || (after !== undefined && !isRepositoryPath(after))) return false;
    const validCount = (count: unknown) => count === undefined || typeof count === 'number' && Number.isSafeInteger(count) && count >= 0;
    if (!validCount('added' in value ? value.added : undefined) || !validCount('removed' in value ? value.removed : undefined)) return false;
    return value.kind === 'added-or-modified' ? before === undefined && after !== undefined
        : value.kind === 'deleted' ? before !== undefined && after === undefined
        : value.kind === 'renamed' ? before !== undefined && after !== undefined && before !== after
        : value.kind === 'modified' && before !== undefined && before === after;
}

export function isAgentChangeset(value: unknown): value is AgentChangeset {
    return !!value && typeof value === 'object'
        && 'id' in value && typeof value.id === 'string' && ['session', 'turn', 'uncommitted'].includes(value.id)
        && 'status' in value && typeof value.status === 'string' && ['loading', 'computing', 'recomputing', 'ready', 'error'].includes(value.status)
        && 'files' in value && Array.isArray(value.files) && value.files.every(isFileChange)
        && 'unmappedFiles' in value && typeof value.unmappedFiles === 'number' && Number.isSafeInteger(value.unmappedFiles) && value.unmappedFiles >= 0
        && (!('turnId' in value) || value.turnId === undefined || typeof value.turnId === 'string')
        && (!('observedAt' in value) || value.observedAt === undefined || typeof value.observedAt === 'string')
        && (!('error' in value) || value.error === undefined || typeof value.error === 'string');
}

export function selectedAgentChanges(state: {
    view: { transport?: 'ahp'; changesets?: AgentChangeset[] } | null;
    changesetId?: string; loading: boolean; reconnecting?: boolean;
}): AgentChangeset | undefined {
    if (state.loading || state.reconnecting || state.view?.transport !== 'ahp') return undefined;
    return state.view.changesets?.find(changes => changes.id === (state.changesetId ?? 'session'));
}

export function changeFileText(file: AgentFileChange): string {
    const paths = file.kind === 'renamed' ? `${file.beforePath} -> ${file.afterPath}` : file.afterPath ?? file.beforePath;
    const stats = file.added !== undefined || file.removed !== undefined
        ? ` (+${file.added ?? '?'}/-${file.removed ?? '?'})` : '';
    return `${file.kind}: ${paths}${stats}`;
}

export function changesetSummary(changes: AgentChangeset): string {
    const meaning = changes.id === 'uncommitted'
        ? 'Includes pre-existing and external edits. Not agent attribution. External edits may not refresh until host activity.'
        : 'Host-recorded file-tool edits only. Shell edits may be missing; paths describe recorded history, not current existence.';
    return [
        CHANGESET_LABELS[changes.id],
        `Host status: ${changes.status}. ${changes.files.length} file(s).`,
        changes.status === 'recomputing' ? 'Showing the previous completed result while the host recomputes.' : '',
        changes.status === 'error' ? 'Scene highlights are suspended. Listed files are the last received state, not a current completed result.' : '',
        changes.observedAt ? `Snapshot received: ${changes.observedAt}. Not a filesystem freshness guarantee.` : 'Waiting for a host snapshot.',
        meaning, changes.error,
        'An after-only entry means added or modified; the protocol does not always distinguish these.',
        changes.unmappedFiles ? `${changes.unmappedFiles} file(s) could not be mapped to this repository; not highlighted.` : '',
        'Core-space: cyan files; amber containing folders for absent/hidden files. Layout filters remain unchanged.',
    ].filter(Boolean).join('\n');
}
